import { ObjectId } from '@racemap/sdk/schema/base';
import { LeaderboardType, VisibilityStates } from '@racemap/utilities/consts/events';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { currentEventTimes } from '@racemap/utilities/functions/event';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Spin } from 'antd';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { LeaderboardHelmet } from 'src/components/Leaderboard/LeaderboardHelmet';
import { useLeaderboardEnvironmentInfo } from 'src/components/Leaderboard/utils';
import { useIsPageVisible } from 'src/lib/customHooks/useIsPageVisible';
import { useSetInterval } from 'src/lib/customHooks/useSetInterval';
import { getLeaderboardUrl } from 'src/lib/getLeaderboardUrl';
import {
  useIsLoading,
  useLeaderboardActions,
  useRelevantEventsData,
  useSelectedEventData,
} from 'src/store/leaderboard/leaderboard_reducer';
import { trackEvent } from '../lib/googleAnalytics';
import { OuterLeaderBoard } from './OuterLeaderBoard';

type Params = {
  eventSlug: string;
};

const RELOAD_INTERVAL = OneMinuteInMillis;

export function LeaderboardView() {
  const { eventSlug } = useParams<Params>();

  const relevantEvents = useRelevantEventsData();
  const loading = useIsLoading();
  const selectedEventData = useSelectedEventData();
  const { isMobile, isEmbedded } = useLeaderboardEnvironmentInfo();
  const selectedEvent = selectedEventData?.eventObject;

  const { loadEventData, changeSelectedEvent, registerLoad } = useLeaderboardActions();
  const isTabVisible = useIsPageVisible();

  const design = isEmbedded || isMobile ? 'compact' : 'standard';
  const [, endTime] = selectedEvent != null ? currentEventTimes(selectedEvent) : [];
  const eventIsOver = endTime != null && endTime < Date.now();
  const isArchived = selectedEvent?.visibility === VisibilityStates.ARCHIVED;
  const loadInterval = isTabVisible && !eventIsOver && !isArchived ? RELOAD_INTERVAL : null;
  const navigate = useNavigate();

  useEffect(() => {
    if (eventSlug == null) return;
    loadEventData(eventSlug);
  }, [eventSlug]);

  useEffect(() => {
    const event = Array.from(relevantEvents.values()).find((e) => e.eventObject.slug === eventSlug);
    if (event != null) {
      changeSelectedEvent(event.eventObject.id);
    } else if (relevantEvents.size > 0) {
      const firstEvent = relevantEvents.values().next().value;
      if (firstEvent == null) return;

      changeSelectedEvent(firstEvent.eventObject.id);
    }

    registerLoad();
  }, [relevantEvents]);

  useEffect(() => {
    const slugOfSelectedEvent = selectedEvent?.slug;
    // in case that the event slug is an object id, but the event has a proper slug, we redirect to the proper slug
    if (
      selectedEvent != null &&
      slugOfSelectedEvent != null &&
      ObjectId.isValid(eventSlug || '') &&
      slugOfSelectedEvent !== eventSlug
    ) {
      navigate(getLeaderboardUrl(selectedEvent, { onlyPath: true }), { replace: true });
    }
  }, [selectedEventData, eventSlug]);

  useSetInterval(() => eventSlug && loadEventData(eventSlug), loadInterval);

  const handleSelectedEventChange = (newEventId: string, newSlug?: string) => {
    trackEvent('leaderboard_event_switch');
    navigate(
      getLeaderboardUrl({ id: newEventId, slug: newSlug } as RacemapEvent, { onlyPath: true }),
    );
  };

  // If we land on this page but set a external in the config
  if (
    selectedEvent != null &&
    selectedEvent.modules.timing.leaderboard.type !== LeaderboardType.DEFAULT
  ) {
    // trigger a full page reload, so that gears-api can delegate to the external leaderboard
    window.location.href = getLeaderboardUrl(selectedEvent);
  }

  return (
    <Spin spinning={loading} style={{ height: '100vh' }}>
      <LeaderboardHelmet />
      <OuterLeaderBoard
        design={design}
        onSelectedEventChange={handleSelectedEventChange}
        containerHeight="100vh"
      />
    </Spin>
  );
}
